import React from "react";
import { Nav, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router";
import RequireAuth from "../../../auth/middleware/RequireAuth";
import { IconChevronDown } from "@tabler/icons-react";
import { usePropertyReadOnly } from "../../hooks/usePropertyReadOnly";

interface SidebarLinkProps {
    link: {
        name: string;
        href: string;
        icon: React.FC;
        permissions?: string[];
        roles?: string[];
        /** Menu item disabled (not hidden) while the active property is inactive. */
        disabledWhenPropertyInactive?: boolean;
        child?: {
            name: string;
            href: string;
            icon: React.FC;
            permissions?: string[];
            roles?: string[];
            disabledWhenPropertyInactive?: boolean;
        }[];
    };
    index: number;
    isOpen: boolean;
    onToggle: () => void;
    onAction?: (action: string) => void;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({ link, index, isOpen, onToggle, onAction }) => {
    const { isReadOnly, reason } = usePropertyReadOnly();

    const handleClick = (e: React.MouseEvent, href: string) => {
        if (href.startsWith('ACTION_')) {
            e.preventDefault();
            onAction?.(href);
        }
    }

    /** Muted, non-navigating placeholder for a menu item disabled by the property-inactive gate. */
    const DisabledNavItem: React.FC<{ name: string; icon: React.FC; className?: string }> = ({ name, icon: Icon, className }) => (
        <OverlayTrigger placement="right" overlay={<Tooltip>{reason}</Tooltip>}>
            <span className={`nav-link d-flex align-items-center text-muted ${className ?? ''}`} style={{ cursor: 'not-allowed', opacity: 0.6 }}>
                <span className="icon-circle">
                    <Icon />
                </span>
                <span className="ms-2">{name}</span>
            </span>
        </OverlayTrigger>
    );

    // If the link has no children, render it as a simple link
    if (!link.child) {
        return (
            <div className={`sidebar-section ${index !== 0 ? 'mt-3' : ''}`}>
                <div className="section-title">{link.name}</div>
                <RequireAuth requiredRoles={link.roles ?? ['dev']} requiredPermissions={link.permissions} asComponent>
                    {link.disabledWhenPropertyInactive && isReadOnly ? (
                        <DisabledNavItem name={link.name} icon={link.icon} />
                    ) : link.href.startsWith('ACTION_') ? (
                        <a
                            href="#"
                            onClick={(e) => handleClick(e, link.href)}
                            className="nav-link d-flex align-items-center text-primary"
                            style={{ textDecoration: 'none' }}
                        >
                            <span className="icon-circle">
                                <link.icon />
                            </span>
                            <span className="ms-2">{link.name}</span>
                        </a>
                    ) : (
                        <Link
                            to={link.href}
                            onClick={(e) => handleClick(e, link.href)}
                            className="nav-link d-flex align-items-center text-primary"
                        >
                            <span className="icon-circle">
                                <link.icon />
                            </span>
                            <span className="ms-2">{link.name}</span>
                        </Link>
                    )}
                </RequireAuth>
            </div>
        )
    }


    // If the link has children, render it as a collapsible menu
    return (
        <div className={`sidebar-section ${index !== 0 ? 'mt-3' : ''}`}>
            <RequireAuth requiredRoles={link.roles ?? ['dev']} requiredPermissions={link.permissions} asComponent>
                {/* The parent link acts as a button to toggle the dropdown */}
                <div
                    onClick={onToggle}
                    className={`section-title d-flex justify-content-between align-items-center ${link.disabledWhenPropertyInactive && isReadOnly ? 'text-muted' : ''}`}
                    style={{ cursor: 'pointer', opacity: link.disabledWhenPropertyInactive && isReadOnly ? 0.6 : 1 }}
                >
                    {link.name}
                    <IconChevronDown
                        size={16}
                        style={{
                            transform: `rotate(${isOpen ? '180deg' : '0'})`,
                            transition: 'transform 0.2s',
                        }}
                    />
                </div>

                {/* Conditionally render children based on the 'isOpen' state */}
                {isOpen && link.child && (
                    <Nav.Item className="d-flex flex-column">
                        {link.child.map((child, childIndex) => (
                            <RequireAuth requiredRoles={child.roles ?? link.roles ?? ['dev']} requiredPermissions={child.permissions} asComponent key={childIndex}>
                                {child.disabledWhenPropertyInactive && isReadOnly ? (
                                    <DisabledNavItem name={child.name} icon={child.icon} className="ps-3" />
                                ) : child.href.startsWith('ACTION_') ? (
                                    <a
                                        href="#"
                                        onClick={(e) => handleClick(e, child.href)}
                                        className="nav-link d-flex align-items-center text-primary ps-3"
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <span className="icon-circle">
                                            <child.icon />
                                        </span>
                                        <span className="ms-2">{child.name}</span>
                                    </a>
                                ) : (
                                    <Link
                                        to={child.href}
                                        onClick={(e) => handleClick(e, child.href)}
                                        className="nav-link d-flex align-items-center text-primary ps-3"
                                    >
                                        <span className="icon-circle">
                                            <child.icon />
                                        </span>
                                        <span className="ms-2">{child.name}</span>
                                    </Link>
                                )}
                            </RequireAuth>
                        ))}
                    </Nav.Item>
                )}
            </RequireAuth>
        </div>
    );
};

export default SidebarLink;